import React, { useCallback, useEffect } from 'react';
import { Snackbar, Typography } from '@mui/material';
import { Alert } from '@mui/material';
import classNames from 'classnames';
import { SnackbarOrigin } from '@mui/material/Snackbar';
import { useMessage } from 'modules/intl';
import { SlideTransition } from 'modules/common/components';
import {
  SnackbarProps,
  useSnackbarStyles
} from 'modules/common/hooks/useSnackbarStyles';
import { isWithinAnotherApp } from 'containerApp';
import { NotesToastContext } from './NotesToastContextProvider';

export type ShowSuccessToast = (showSuccess: boolean, message: string) => void;
export type ShowErrorToast = (showSuccess: boolean) => void;

const anchorOrigin: SnackbarOrigin = { vertical: 'bottom', horizontal: 'left' };

export type NotesToastContextType = {
  toastContext: NotesToastContext;
  clearToastContext: (id: string) => void;
  dismiss: boolean;
  offset?: string | number;
} & SnackbarProps;

export const NoteToast: React.FC<NotesToastContextType> = ({
  pinned = true,
  toastContext: {
    id,
    message,
    showError,
    showSuccess,
    showMessage,
    values,
    messageElement
  },
  clearToastContext,
  dismiss,
  offset = 20
}: NotesToastContextType) => {
  const [open, setOpen] = React.useState(false);
  const snackbarClasses = useSnackbarStyles({
    pinned
  });
  const snackbarErrorClasses = useSnackbarStyles({
    pinned,
    category: 'error'
  });
  const snackbarInfoClasses = useSnackbarStyles({
    pinned,
    category: 'info'
  });

  const handleClose = useCallback(() => {
    setOpen(false);
    clearToastContext(id ?? 'default-id');
  }, [clearToastContext, id]);

  useEffect(() => {
    if (showSuccess || showError || showMessage) {
      setOpen(true);
    }
  }, [showSuccess, showError, showMessage]);

  useEffect(() => {
    if (dismiss) {
      handleClose();
    }
  }, [dismiss, handleClose]);

  const intlMessage = useMessage(message, values);

  return (
    <Snackbar
      anchorOrigin={anchorOrigin}
      open={open}
      autoHideDuration={3000}
      onClose={handleClose}
      className={snackbarClasses.snackBar}
      TransitionComponent={SlideTransition}
      style={{ bottom: offset }}
    >
      <div>
        {showMessage ? (
          <Alert
            severity="info"
            variant="filled"
            data-testid="note-toast-message-info"
            className={classNames(
              snackbarClasses.item,
              snackbarInfoClasses.severity
            )}
            aria-label={isWithinAnotherApp() ? intlMessage : ''}
          >
            {messageElement ? (
              messageElement
            ) : (
              <Typography variant="body1">{intlMessage}</Typography>
            )}
          </Alert>
        ) : null}
        {showSuccess ? (
          <Alert
            severity="success"
            variant="filled"
            data-testid="note-toast-message-success"
            className={classNames(
              snackbarClasses.item,
              snackbarClasses.severity
            )}
            aria-label={isWithinAnotherApp() ? intlMessage : ''}
          >
            {messageElement ? (
              messageElement
            ) : (
              <Typography variant="body1">{intlMessage}</Typography>
            )}
          </Alert>
        ) : null}
        {showError ? (
          <Alert
            severity="error"
            variant="filled"
            data-testid="note-toast-message-failure"
            className={classNames(
              snackbarClasses.item,
              snackbarErrorClasses.severity
            )}
            aria-label={isWithinAnotherApp() ? intlMessage : ''}
          >
            {messageElement ? (
              messageElement
            ) : (
              <Typography variant="body1">{intlMessage}</Typography>
            )}
          </Alert>
        ) : null}
      </div>
    </Snackbar>
  );
};
